import { ShieldQuestion } from 'lucide-react';
import { useSession } from '@/components/auth/session';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { useReview, useStats } from '@/hooks/use-fleet';
import { cn } from '@/lib/utils';

function OrgStat({ label, value, tone }: { label: string; value: string | number; tone?: string }) {
  return (
    <div className="min-w-0 px-gutter py-3.5">
      <div className="dy-eyebrow truncate">{label}</div>
      <div className={cn('dy-figure mt-1.5 text-3xl leading-none', tone)}>{value}</div>
    </div>
  );
}

/**
 * The org's figures, and why they are what this person is looking at.
 *
 * Two different absences share the region because the fix for both is the same
 * shape. `undecided` means several members answer to this name and the hub
 * declined to pick one, so nothing has been attributed; otherwise this person
 * simply owns nothing yet. Showing one person another person's devices and jobs
 * would be worse than showing neither, so the page shows the org until the name
 * is unambiguous.
 *
 * The page only renders this when it is unlocked, so the org reads are asked
 * unconditionally here.
 */
export function UnmatchedNotice({ undecided }: { undecided: boolean }) {
  const { session } = useSession();
  const stats = useStats();
  const review = useReview();
  const metrics = review.data?.metrics;

  return (
    <Section>
      <SectionHead
        title={undecided ? 'We could not tell which member you are' : 'Nothing here is yours yet'}
        hue="var(--dy-warn)"
      />
      <Surface>
        <div className="flex items-start gap-3 px-gutter py-4">
          <ShieldQuestion className="text-warn mt-0.5 size-4 shrink-0" aria-hidden />
          <div className="min-w-0 space-y-2">
            {undecided ? (
              <>
                <p className="text-fg-2 text-base">
                  More than one person on this hub answers to “{session.actor}”, so nothing has been
                  attributed to you. Sign in with your account and the hub will name you instead of
                  matching a string.
                </p>
                <p className="text-fg-3 text-sm">
                  Showing one person another person’s devices and jobs would be worse than showing
                  neither, so this page shows the org until the name is unambiguous.
                </p>
              </>
            ) : (
              <>
                <p className="text-fg-2 text-base">
                  No enrolled device and no job opened under this name, so this page shows what the
                  whole org is doing instead. Connect a laptop on Agents, which mints your own code,
                  or open a job and it will appear here.
                </p>
                <p className="text-fg-3 text-sm">
                  Names are matched against the email, display name, first name or address prefix on
                  your user record.
                </p>
              </>
            )}
          </div>
        </div>
        <div className="divide-line grid grid-cols-2 divide-x divide-y border-t sm:grid-cols-4 sm:divide-y-0">
          <OrgStat label="In flight" value={stats.data?.tasks.inFlight ?? '-'} />
          <OrgStat label="Queued" value={stats.data?.queue.queued ?? '-'} />
          <OrgStat label="In review" value={metrics?.inReview ?? '-'} tone="text-gate" />
          <OrgStat label="Merged" value={metrics?.merged.count ?? '-'} tone="text-ok" />
        </div>
      </Surface>
    </Section>
  );
}
